import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { getResearchJob, sendDigest, getDigestStatus } from "../lib/api";
import { API } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ScanningPanel from "../components/ScanningPanel";
import { ArrowLeft, CheckCircle2, AlertCircle, Download, Mail } from "lucide-react";

export default function ResearchJobPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [digestCfg, setDigestCfg] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    getDigestStatus().then(setDigestCfg).catch(() => setDigestCfg({ configured: false }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const data = await getResearchJob(jobId);
        if (cancelled) return;
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load job");
      }
    };
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 2000);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
    };
  }, [jobId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-black mb-2">Could not load job</h2>
        <p className="text-zinc-600 mb-6">{error}</p>
        <Link to="/admin" className="btn-outline-dark inline-block">Back to Dashboard</Link>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="micro-label mb-4">LOADING JOB...</div>
      </div>
    );
  }

  const isRunning = !["completed", "failed"].includes(job.status);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
      <div className="flex items-center justify-between mb-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 micro-label hover:text-zinc-900"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} strokeWidth={2.5} /> Back
        </Link>
        <div className="micro-label">JOB ID · {job.id.slice(0, 8).toUpperCase()}</div>
      </div>

      <div className="border border-zinc-200 p-8 md:p-12 mb-10">
        <div className="micro-label mb-3">
          NICHE ·{" "}
          {job.niche === "home_decor" ? "HOME DECOR" : "GADGETS / TECH"}
        </div>
        <h1
          className="text-4xl md:text-6xl font-black tracking-tighter mb-4"
          style={{ fontFamily: "Outfit" }}
          data-testid="job-heading"
        >
          {job.status === "completed" ? "3 Winners Identified." : "Agent Running..."}
        </h1>
        <p className="text-zinc-600 max-w-2xl leading-relaxed">
          {job.status === "completed"
            ? "Claude Sonnet 4.5 analyzed trend signals and ranked the top problem-solving products for the Ontario market."
            : "The research agent is actively scanning public trend sources. This typically takes 20–60 seconds."}
        </p>
      </div>

      {isRunning && <ScanningPanel job={job} />}

      {job.status === "failed" && (
        <div className="border border-red-300 bg-red-50 p-8 mb-10">
          <div className="flex items-start gap-3">
            <AlertCircle size={28} className="text-red-500 shrink-0 mt-1" />
            <div>
              <div className="font-bold text-red-900 mb-1">Research failed</div>
              <div className="text-sm text-red-700">{job.error || "Unknown error"}</div>
            </div>
          </div>
        </div>
      )}

      {job.status === "completed" && job.products?.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={22} strokeWidth={2.5} className="text-emerald-500" />
              <span className="micro-label">ANALYSIS COMPLETE</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {digestCfg?.configured && (
                <button
                  onClick={async () => {
                    setSendingDigest(true);
                    try {
                      await sendDigest(job.id);
                      toast.success(`Digest sent to ${digestCfg.recipient}`);
                    } catch (e) {
                      toast.error(
                        "Could not send digest",
                        { description: e?.response?.data?.detail || e?.message }
                      );
                    } finally {
                      setSendingDigest(false);
                    }
                  }}
                  disabled={sendingDigest}
                  data-testid="send-digest-btn"
                  className="btn-outline-dark inline-flex items-center gap-2 text-xs"
                >
                  <Mail size={14} strokeWidth={2.5} />
                  {sendingDigest ? "SENDING..." : "EMAIL DIGEST"}
                </button>
              )}
              <a
                href={`${API}/research/${job.id}/export`}
                download
                data-testid="export-csv-btn"
                className="btn-outline-dark inline-flex items-center gap-2 text-xs"
              >
                <Download size={14} strokeWidth={2.5} />
                EXPORT CSV
              </a>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-0 border border-zinc-200" data-testid="products-grid">
            {job.products.map((p, idx) => (
              <ProductCard key={p.id} product={p} rank={idx + 1} last={idx === job.products.length - 1} />
            ))}
          </div>

          {job.scraped_hashtags?.length > 0 && (
            <div className="mt-10 border border-zinc-200 p-6">
              <div className="micro-label mb-3">TREND SIGNALS CAPTURED ({job.scraped_hashtags.length})</div>
              <div className="flex flex-wrap gap-2">
                {job.scraped_hashtags.slice(0, 40).map((t) => (
                  <span
                    key={t}
                    className="text-xs font-mono px-2 py-1 border border-zinc-300 text-zinc-700"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
